import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listEvalExperimentsOptions,
  listEvalFixturesOptions,
  updateEvalFixtureMutation,
} from '@/api/generated/@tanstack/react-query.gen';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { AddSessionDialog } from '@/components/Evals/AddSessionDialog';
import { EvalShell, Panel } from '@/components/Evals/EvalShell';
import { Cover } from '@/components/Evals/Cover';
import { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';

export const Route = createFileRoute('/_auth/evals/sessions')({
  component: SessionsUnderTestPage,
});

function SessionsUnderTestPage() {
  const queryClient = useQueryClient();
  const fixtures = useQuery(listEvalFixturesOptions());
  const experiments = useQuery(listEvalExperimentsOptions());
  const update = useMutation({
    ...updateEvalFixtureMutation(),
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const experimentName = (id?: string | null) =>
    experiments.data?.find((e) => e.id === id)?.name;

  return (
    <EvalShell
      title="Sessions under test"
      subtitle="Lessons frozen as generated, so every experiment plays the same one."
      actions={<AddSessionDialog />}
    >
      <Panel>
        {!fixtures.data ? (
          <Skeleton className="h-40 rounded-xl" />
        ) : (
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-neutral-400">
                <th className="py-1.5 pr-3 font-medium">Session</th>
                <th className="py-1.5 pr-3 font-medium">Baseline</th>
                <th className="py-1.5 pr-3 font-medium">Learner</th>
                <th className="py-1.5 text-right font-medium">Active</th>
              </tr>
            </thead>
            <tbody>
              {fixtures.data.map((f) => (
                <tr
                  key={f.id}
                  className="border-t border-neutral-100 align-top"
                >
                  <td className="flex items-center gap-3 py-2.5 pr-3">
                    <Cover url={f.cover_url} size={40} />
                    <div>
                      <p className="font-medium text-neutral-900">{f.name}</p>
                      <p className="text-[11px] text-neutral-400">
                        {f.use_case || 'session'} · {f.key} ·{' '}
                        <Link
                          to="/sessions/$id"
                          params={{ id: f.session_template_id }}
                          className="hover:underline"
                        >
                          open lesson
                        </Link>
                      </p>
                    </div>
                  </td>
                  <td className="py-2.5 pr-3">
                    {f.baseline_experiment_id ? (
                      <Link
                        to="/evals/experiments/$experimentId"
                        params={{ experimentId: f.baseline_experiment_id }}
                        className="font-medium text-neutral-700 hover:underline"
                      >
                        {experimentName(f.baseline_experiment_id) ?? 'Baseline'}
                      </Link>
                    ) : (
                      <span className="text-neutral-400">Not set</span>
                    )}
                  </td>
                  <td className="max-w-md py-2.5 pr-3 text-[12px] text-neutral-500">
                    <LearnerProfile
                      value={f.learner_profile ?? ''}
                      onSave={(learner_profile) =>
                        update.mutate({ path: { fixture_id: f.id }, body: { learner_profile } })
                      }
                    />
                    {f.notes && <p className="mt-1 text-neutral-400">{f.notes}</p>}
                  </td>
                  <td className="py-2.5 text-right">
                    <Switch
                      checked={f.active ?? true}
                      onCheckedChange={(active) =>
                        update.mutate({
                          path: { fixture_id: f.id },
                          body: { active },
                        })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </EvalShell>
  );
}

/** Who the simulated learner is for this session (the ICP); saved when the field loses focus. */
function LearnerProfile({ value, onSave }: { value: string; onSave: (value: string) => void }) {
  const [draft, setDraft] = useState(value);
  return (
    <Textarea
      rows={2}
      value={draft}
      placeholder="Who the learner is (default: an adult beginner)"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => draft !== value && onSave(draft)}
      className="min-h-0 resize-none border-transparent bg-transparent px-1 py-0.5 text-[12px] shadow-none hover:border-neutral-200 focus:border-neutral-300"
    />
  );
}
